import { Button, Group, Radio, Stack, TextInput, useMantineTheme } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { hasAtLeast, isEmpty, keys, last } from 'remeda';

import type { ClientForm } from '../hooks/useClientForm';

import { FieldRemoveControl } from './FieldRemoveControl';

export type EmailAddress = {
  email: string;
  isDefault: boolean;
};

type MultiEmailInputProps = {
  form: ClientForm;
};

export const MultiEmailInputDefault = ({ form }: MultiEmailInputProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { emails } = form.values;

  const shouldShowError = keys(form.errors).some(
    key => key.startsWith('emails.') && key.includes('.email'),
  );

  const addEmailInput = () => {
    form.insertListItem('emails', {
      email: '',
      isDefault: false,
    });
  };

  const setDefaultEmail = (emailIndex: number) => {
    const updatedEmails = emails.map((emailItem, index) => ({
      ...emailItem,
      isDefault: index === emailIndex,
    }));

    form.setFieldValue('emails', updatedEmails);
  };

  const canAddEmail = () => {
    if (!hasAtLeast(emails, 1)) return false;

    const lastEmail = last(emails).email.trim();

    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/;
    return !emailRegex.test(lastEmail);
  };

  const emailInputFields = emails.map((emailItem, index) => (
    <Group key={`email-${index}`} align="start" gap="sm">
      <Radio
        key={form.key(`emails.${index}.isDefault`)}
        checked={emailItem.isDefault}
        disabled={isEmpty(emailItem.email.trim())}
        onChange={() => setDefaultEmail(index)}
        mt="xl"
      />
      <TextInput
        {...form.getInputProps(`emails.${index}.email`)}
        key={form.key(`emails.${index}.email`)}
        flex={1}
        onChange={event => {
          form.setFieldValue(`emails.${index}.email`, event.currentTarget.value);
        }}
        label={`${t(i18nKeys.EMAIL)} ${index + 1}${emailItem.isDefault ? ' (default)' : ''}`}
        placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.EMAIL)}
        rightSection={
          <FieldRemoveControl
            data={emails}
            disabled={isEmpty(emailItem.email.trim())}
            onClear={() => form.setFieldValue(`emails.${index}.email`, '')}
            onRemove={() => form.removeListItem('emails', index)}
          />
        }
      />
    </Group>
  ));

  return (
    <Panel
      title={t(i18nKeys.EMAILS)}
      style={{ borderColor: shouldShowError ? theme.colors.error[5] : theme.colors.gray[2] }}
    >
      <Stack gap="sm">
        {emailInputFields}
        <Group justify="end" mt="lg">
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={addEmailInput}
            size="sm"
            disabled={canAddEmail()}
          >
            {t(i18nKeys.FORM.CLIENT.ADD_EMAIL)}
          </Button>
        </Group>
      </Stack>
    </Panel>
  );
};
